import { ARENA } from '../../config/constants.js';

const CONTACT_COOLDOWN_DURATION = 0.5;
const HIT_FLASH_DURATION = 0.08;

/**
 * Tollkeeper (Act III, ACT3-BIBLE.md §8) — a Tithe echo, and the only minion in
 * the game that doesn't threaten your health at all.
 *
 * It absorbs XP motes and pickups inside its radius and holds them. Kill it and
 * it drops the lot; ignore it and it walks off the far side of the arena with
 * your run's economy in its pockets. So it costs you *the run's income*, not
 * your HP — which is the Tithe's whole argument, played for pennies instead of
 * for a health bar, and the one bit of the act that asks "what are you willing
 * to give up" before the boss asks it with a straight face.
 *
 * `absorb` is called by PickupSystem, which owns the motes; this class only
 * holds the tally and hands it back on death (`bankedXp`, read by Game's
 * minion:died handler).
 */
export class Tollkeeper {
  constructor(eventBus, { x, y, radius = 5, hp, moveSpeed, contactDamage, absorbRadius = 72 }) {
    this.eventBus = eventBus;
    this.type = 'tollkeeper';
    this.x = x;
    this.y = y;
    this.radius = radius;
    this.maxHp = hp;
    this.hp = hp;
    this.moveSpeed = moveSpeed;
    this.contactDamage = contactDamage;
    this.absorbRadius = absorbRadius;
    // Everything it has taken, paid back in full when it dies.
    this.bankedXp = 0;
    this.isDead = false;
    this._contactCooldown = 0;
    this.hitFlashTimer = 0;
  }

  /** Called by PickupSystem when a mote is inside the absorb radius. */
  absorb(xp) {
    this.bankedXp += xp;
    this.eventBus.emit('tollkeeper:absorbed', { x: this.x, y: this.y, xp, total: this.bankedXp });
  }

  update(dt, player) {
    this.hitFlashTimer = Math.max(0, this.hitFlashTimer - dt);

    // It doesn't hunt: it patrols slowly toward the arena's centre of gravity,
    // where the motes are. Its threat is entirely that it is somewhere else,
    // getting fatter, while you deal with the things that do hunt.
    const cx = ARENA.x + ARENA.width / 2;
    const cy = ARENA.y + ARENA.height / 2;
    const dx = cx - this.x;
    const dy = cy - this.y;
    const dist = Math.hypot(dx, dy) || 1;
    if (dist > 40) {
      this.x += (dx / dist) * this.moveSpeed * dt;
      this.y += (dy / dist) * this.moveSpeed * dt;
    }
    this._clampToArena();

    this._contactCooldown = Math.max(0, this._contactCooldown - dt);
    const playerDist = Math.hypot(player.x - this.x, player.y - this.y);
    if (this._contactCooldown <= 0 && playerDist < this.radius + player.radius) {
      player.takeDamage(this.contactDamage, this.type);
      this._contactCooldown = CONTACT_COOLDOWN_DURATION;
    }
  }

  _clampToArena() {
    this.x = Math.min(ARENA.x + ARENA.width - this.radius, Math.max(ARENA.x + this.radius, this.x));
    this.y = Math.min(ARENA.y + ARENA.height - this.radius, Math.max(ARENA.y + this.radius, this.y));
  }

  takeDamage(amount) {
    if (this.isDead) return;
    const hpBefore = this.hp;
    this.hp = Math.max(0, this.hp - amount);
    this.hitFlashTimer = HIT_FLASH_DURATION;
    this.eventBus.emit('minion:damaged', { amount: hpBefore - this.hp, x: this.x, y: this.y, minion: this });
    if (this.hp <= 0) {
      this.isDead = true;
      this.eventBus.emit('minion:died', { x: this.x, y: this.y, minion: this });
    }
  }
}
